import {
  useFeatureFlagPayload,
  useFeatureFlagVariantKey,
} from "posthog-js/react";

import { posthogClient } from "./posthog";

/**
 * PostHog feature flag registry — the single source of truth for every flag
 * the frontend reads.
 *
 * Why this file exists: PR #3193 (Danone AskPuntt outage). The flag was
 * created as a Boolean in the PostHog UI so `getFeatureFlag()` returned
 * `true`, but the call site compared against the string `"enabled"` and
 * silently blocked every user. Every flag read now goes through the typed
 * accessors below, which normalize PostHog's polymorphic
 * `boolean | string | undefined` return so boolean-vs-multivariate mismatches
 * cannot break prod again.
 *
 * Adding a flag:
 *   1. Add an entry to FLAGS below with `kind: "boolean" | "variant" | "payload"`.
 *   2. Read it via `isFlagOn` / `useIsFlagOn` (boolean), `flagVariant` /
 *      `useFlagVariant` (variant), or `flagPayload` / `useFlagPayload` (payload).
 *   3. Never import `posthog-js` feature-flag APIs directly — ESLint enforces this.
 */

type BooleanFlagDef = {
  kind: "boolean";
  description?: string;
};

type VariantFlagDef<V extends string = string> = {
  kind: "variant";
  variants: readonly V[];
  description?: string;
};

type PayloadFlagDef<P = unknown> = {
  kind: "payload";
  description?: string;
  _payload?: P;
};

export const FLAGS = {
  "danone-beta-testers": {
    kind: "boolean",
    description: "Grants Danone users early access to AskPuntt",
  },
  "ask-puntt-v2": {
    kind: "boolean",
    description:
      "In-place Ask Puntt V2. After login on app.puntt.ai, flagged users reload onto the V2 tree on the same origin (same localStorage session — no second login). Create as Boolean in PostHog.",
  },
  "puntt-access": { kind: "boolean" },
  "ask-puntt-warhol": {
    kind: "boolean",
    description:
      "Route NEW Ask Puntt chats to the warhol agent instead of rembrandt. Existing chats keep the agent they were created with. Create as Boolean in PostHog.",
  },
  "dismiss-comments": { kind: "boolean" },
  "resolve-comments": { kind: "boolean" },
  "delete-comments": { kind: "boolean" },
  "ai-thumbs-down": { kind: "boolean" },
  "create-revisions": { kind: "boolean" },
  "show-dismissed-comments": { kind: "boolean" },
  "manage-brand-guidelines": { kind: "boolean" },
  "manage-users": { kind: "boolean" },
  "restricted-comment-edit": { kind: "boolean" },
  "comment-personas": { kind: "boolean" },
  "puntt-dashboard": { kind: "boolean" },
  "run-ai-review": {
    kind: "variant",
    variants: ["always", "first", "none"] as const,
    description: "Controls which users can trigger AI reviews",
  },
  "knowledge-database": {
    kind: "variant",
    variants: [
      "view",
      "disabled",
      "manage",
      // Legacy tiers — map to `manage` in canManageKnowledgeDatabase so
      // existing PostHog rollouts keep working without a flag-day.
      "mutate-records",
      "mutate-models",
    ] as const,
    description:
      "Tiered access to the knowledge database (view → manage). `manage` gates uploads, scope edits, promotions, and deletes; legacy `mutate-records`/`mutate-models` map to manage. PostHog variant `view` is the read tier — prior code used `enabled` which never matched in PostHog.",
  },
  "spreadsheet-extraction": {
    kind: "variant",
    variants: ["disabled", "shadow", "full"] as const,
    description:
      "Gates structured spreadsheet extraction across samba/rembrandt/carrie. `disabled` = legacy image pipeline. `shadow` = new pipeline runs in parallel, diff logged, no customer-visible change. `full` = new pipeline serves the SpreadsheetView. See improvements/2026-05-12_spreadsheet-structured-extraction.md §8.",
  },
  "persona-nicknames": {
    kind: "payload",
  } as PayloadFlagDef<Record<string, string>>,
  "ai-rules-available": {
    kind: "payload",
  } as PayloadFlagDef<Record<string, string>>,
} as const satisfies Record<
  string,
  BooleanFlagDef | VariantFlagDef | PayloadFlagDef
>;

export type FeatureFlagName = keyof typeof FLAGS;

type BooleanFlagName = {
  [K in FeatureFlagName]: (typeof FLAGS)[K]["kind"] extends "boolean"
    ? K
    : never;
}[FeatureFlagName];

type VariantFlagName = {
  [K in FeatureFlagName]: (typeof FLAGS)[K]["kind"] extends "variant"
    ? K
    : never;
}[FeatureFlagName];

type PayloadFlagName = {
  [K in FeatureFlagName]: (typeof FLAGS)[K]["kind"] extends "payload"
    ? K
    : never;
}[FeatureFlagName];

type VariantOf<N extends VariantFlagName> = (typeof FLAGS)[N] extends {
  variants: readonly (infer V)[];
}
  ? V
  : never;

type PayloadOf<N extends PayloadFlagName> =
  (typeof FLAGS)[N] extends PayloadFlagDef<infer P> ? P : unknown;

type RawFlagValue = boolean | string | undefined | null;

// Accept `true` (boolean flag), `"enabled"` and `"true"` (multivariate flag
// where someone named the on-variant `"enabled"` or used PostHog's default
// multivariate-from-boolean migration).
function normalizeBoolean(value: RawFlagValue): boolean {
  if (value === true) return true;
  if (typeof value === "string") {
    return value === "enabled" || value === "true";
  }
  return false;
}

function asVariantKey<N extends VariantFlagName>(
  value: RawFlagValue,
): VariantOf<N> | undefined {
  return typeof value === "string" ? (value as VariantOf<N>) : undefined;
}

/**
 * DEV-ONLY local override for boolean flags. On a local build PostHog is not
 * connected (no `VITE_POSTHOG_KEY`), so every flag reads off. This lets you flip
 * one on for local testing without PostHog:
 *
 *   localStorage.setItem("ff:puntt-dashboard", "1"); // then reload
 *   localStorage.removeItem("ff:puntt-dashboard");   // turn off
 *
 * Returns `undefined` (defer to PostHog) unless an override key exists. Guarded
 * by `import.meta.env.DEV`, so prod builds tree-shake this to a constant `false`
 * branch — production flag reads are unchanged.
 */
// Flags that default ON in local dev builds so demos are visible with no console
// step (PostHog is not connected locally). A `ff:<name>` localStorage key still
// overrides (set "0" to force off). Prod ignores all of this.
const DEV_DEFAULT_ON: FeatureFlagName[] = ["ask-puntt-v2"];

function devFlagOverride(name: FeatureFlagName): boolean | undefined {
  if (!import.meta.env.DEV || typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(`ff:${name}`);
    if (raw != null) return raw === "1" || raw === "true";
  } catch {
    // localStorage disabled — fall through to the dev default.
  }
  return DEV_DEFAULT_ON.includes(name) ? true : undefined;
}

/**
 * Read a boolean flag synchronously (route loaders, redux thunks, imperative
 * code). Prefer `useIsFlagOn` inside React components.
 */
export function isFlagOn(name: BooleanFlagName): boolean {
  const override = devFlagOverride(name);
  if (override !== undefined) return override;
  return normalizeBoolean(posthogClient.getFeatureFlag(name) as RawFlagValue);
}

/**
 * Read a multivariate flag synchronously. Returns one of the declared
 * variants, or `undefined` when the flag has no assignment yet.
 */
export function flagVariant<N extends VariantFlagName>(
  name: N,
): VariantOf<N> | undefined {
  return asVariantKey<N>(posthogClient.getFeatureFlag(name) as RawFlagValue);
}

/** Read a payload flag synchronously. */
export function flagPayload<N extends PayloadFlagName>(
  name: N,
): PayloadOf<N> | undefined {
  return posthogClient.getFeatureFlagPayload(name) as PayloadOf<N> | undefined;
}

// React hooks — re-render when PostHog pushes a flag update.
export function useIsFlagOn(name: BooleanFlagName): boolean {
  // Call the hook unconditionally (rules of hooks); the dev override wins.
  const value = normalizeBoolean(useFeatureFlagVariantKey(name));
  const override = devFlagOverride(name);
  return override !== undefined ? override : value;
}

export function useFlagVariant<N extends VariantFlagName>(
  name: N,
): VariantOf<N> | undefined {
  return asVariantKey<N>(useFeatureFlagVariantKey(name));
}

export function useFlagPayload<N extends PayloadFlagName>(
  name: N,
): PayloadOf<N> | undefined {
  return useFeatureFlagPayload(name) as PayloadOf<N> | undefined;
}
